// Single source of truth for the "General" / top-level bucket — pages
// whose stored `WikiPage.category` is null/empty (e.g. `wiki/index.md`,
// loose root-level pages). The bucket exists as one concept but
// historically had two spellings:
//
//   - `'_top'` — the sidebar's internal grouping sentinel. Predates the
//     `/c/:category` route and only ever appeared in `useCatalogTree`'s
//     groupedPages map. Kept for backward-compat with anything (or
//     anyone) still typing `/c/_top` in the URL bar.
//   - `'general'` — the user-facing URL form. What `/c/general`
//     resolves to and what HomeLanding's chip stores so its identity
//     matches the route.
//
// This module owns both spellings + the resolution helpers so other
// modules don't pattern-match on string literals. Pure helpers — no
// Vue/Pinia deps, safe to import from views, composables, fixtures.

import type { WikiPage } from '../stores/wiki_pages'

// User-facing canonical key. Used in URLs (/c/general), as the chip's
// `raw` identity on HomeLanding, and as the bucket key when grouping
// null-category pages.
export const GENERAL_BUCKET = 'general'

// Sidebar-internal alias. The sidebar's catalog tree groups pages by
// display label and predates the URL convention; keeping the alias
// here means callers don't sprinkle the literal across the codebase.
export const GENERAL_BUCKET_ALIAS = '_top'

// Display label for both keys. Capitalised "General" is what the user
// reads in the sidebar group, the breadcrumb, the chip, and the
// CategoryView heading.
export const GENERAL_BUCKET_LABEL = 'General'

const GENERAL_KEYS = new Set<string>([GENERAL_BUCKET, GENERAL_BUCKET_ALIAS])

// Treat both `general` and `_top` as the General bucket. Comparison is
// case-sensitive on the already-lowercased route param — callers that
// take user input (URL params) should pre-lowercase.
export function isGeneralBucket(key: string): boolean {
  return GENERAL_KEYS.has(key)
}

// Resolve a WikiPage to its bucket key. Pages without a stored
// category — AND pages whose stored category is the legacy `_top`
// literal that the ingest pipeline writes for top-level files — both
// bucket under the user-facing `general` form so chip identity
// matches the URL.
export function pageBucket(page: WikiPage): string {
  if (!page.category || isGeneralBucket(page.category)) return GENERAL_BUCKET
  return page.category
}

// The category key memory pages carry (`memory/<project>/<slug>.md` →
// `category: "memory"`). Memory is browsed exclusively through the
// dedicated `/memory` view, so the generic catalog surfaces —
// the sidebar tree, the home recently-updated grid, and the
// category-chip strip — exclude memory pages via `isMemoryPage`. One
// predicate so that exclusion lives in a single place.
export const MEMORY_BUCKET = 'memory'

export function isMemoryPage(page: WikiPage): boolean {
  return page.category === MEMORY_BUCKET
}

// Entities/ pages classified `type: person` in frontmatter (#1655) get
// their own "Persons" sidebar group instead of folding into the
// generic "Entities" bucket alongside orgs/products/projects. Unlike
// `isMemoryPage`, this doesn't need a dedicated route — the sidebar
// tree's category rows are display-only groupings (non-selectable,
// expand/collapse), so a "Persons" bucket is purely a relabelling of
// where a person page's row renders.
export const PERSON_BUCKET_LABEL = 'Persons'

export function isPersonPage(page: WikiPage): boolean {
  return page.category === 'entities' && page.entity_type === 'person'
}

// Bucket key for the category-filter chip strip (HomeLanding) — mirrors
// the sidebar tree's person/entities split so a person page gets its
// own "Persons" chip instead of folding into "Entities" there too.
// Kept separate from `pageBucket`, which CategoryView's `/c/:category`
// route still uses unsplit: that route already lists every entities/
// page, person or not, on one page.
export const PERSON_BUCKET = 'persons'

export function chipBucket(page: WikiPage): string {
  return isPersonPage(page) ? PERSON_BUCKET : pageBucket(page)
}
